import { colors } from '@prenuvo/shared-library';
import { createContext, useCallback, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import config from '../config';
import { useAuth, useIndicatorsQuery, useSelectedStudy } from '../hooks';
import { makeSliders, SliderType } from '../utils/makeSliders';

export const chosenView = (search: string) => {
  const { view, handleSetView } = useAuth();
  const searchParams = new URLSearchParams(search);
  return useMemo(() => {
    const searchParamsView = searchParams.get('view') as string;
    if (view !== searchParamsView) {
      return handleSetView(searchParamsView);
    }
  }, [search]);
};

type colorsKeys = keyof typeof colors;

export type TableContextType = {
  isOpen: boolean;
  setIsOpen: (params: boolean) => void;
  toggleOpen: () => void;
  selectedStudyId: string | undefined;
  handleRowClick: (studyId: string) => void;
  closeTabs: () => void;
  navigateWithView: (url: string, replace: boolean) => void;
  indicators: {
    [key: string]: { color: string; indicatorNumber: number };
  };
  getIndicators: () => void;
};

export const TableContext = createContext<TableContextType | undefined>(undefined);

const DEFAULT_INNER_TAB = 'member-details';

const TableProvider = ({ children }: { children: React.ReactNode }) => {
  const {
    tab,
    studyId: selectedStudyId,
    innerTab,
  } = useParams<{ tab: string; studyId: string; innerTab: string }>();
  const { isNPCoordinatorOrPrescriber, user, allowedPrescriberLocations, view: authContextView } = useAuth();

  const [isOpen, setIsOpen] = useState(!!selectedStudyId);
  const toggleOpen = useCallback(() => setIsOpen((isOpen) => !isOpen), []);

  const { search } = useLocation();
  const view = chosenView(search);

  const getNavigateUrl = (url: string): string => {
    if (url.includes('prescriptions') && !isNPCoordinatorOrPrescriber) {
      return getNavigateUrl('/401');
    }
    // If the URL is an absolute path (e.g., /401) or doesn't contain study navigation,
    // return it as-is without applying innerTab validation
    if (url.startsWith('/') && !url.includes('/study/')) {
      return `${url}${view ? `?view=${view}` : ''}`;
    }
    if (innerTab) {
      const possibleSliders = makeSliders(tab as string, view !== 'np_coordinator') || [];
      if (!possibleSliders.find((slider: SliderType | undefined) => slider?.id === innerTab)) {
        return `${config.BASE_NAME}/${tab}/study/${selectedStudyId}/${DEFAULT_INNER_TAB}${
          view ? `?view=${view}` : ''
        }`;
      }
    }
    return `${url}${view ? `?view=${view}` : ''}`;
  };

  const navigate = useNavigate();
  const navigateWithView = (url: string, replace: boolean) => {
    navigate(getNavigateUrl(url), { replace: replace });
  };

  const { resetSelectedStudyValues } = useSelectedStudy();

  const closeTabs = useCallback(() => {
    setIsOpen(false);
    const navigateTo = tab ? `${config.BASE_NAME}/${tab}` : config.BASE_NAME;
    navigateWithView(navigateTo, true);
  }, [tab]);

  const handleRowClick = (studyId: string) => {
    if (studyId === selectedStudyId) {
      closeTabs();
    } else {
      resetSelectedStudyValues();
      navigateWithView(`${config.BASE_NAME}/${tab}/study/${studyId}/${DEFAULT_INNER_TAB}`, true);
      setIsOpen(true);
      window.scrollTo(0, 0);
    }
  };

  const [indicators, setIndicators] = useState<{
    [key: string]: { color: string; indicatorNumber: number };
  }>({});

  const studyLocationFilter =
    view === 'np' || authContextView === 'np'
      ? allowedPrescriberLocations.map(({ code }) => code).join(',')
      : 'all';
  const prescriptionIndicatorFilter = {
    status: 'indicator-prescription',
    location: studyLocationFilter,
    dateStart: '0',
    dateEnd: '0',
    ...((view === 'np' || authContextView === 'np') && { selectedNP: user?.userId }),
  };
  const { fetchStudies } = useIndicatorsQuery(prescriptionIndicatorFilter);
  const getIndicators = async () => {
    const { data } = await fetchStudies({
      context: {
        clientName: 'apps',
      },
      variables: {
        studyFilter: prescriptionIndicatorFilter,
      },
    });

    const prescriptions = {
      indicatorNumber: data.npdIndicators.prescriptions.indicatorNumber,
      ...(data.npdIndicators.prescriptions.color && {
        color: colors[data.npdIndicators.prescriptions.color as colorsKeys],
      }),
    };
    setIndicators({ prescriptions });
  };

  return (
    <TableContext.Provider
      value={{
        isOpen,
        setIsOpen,
        toggleOpen,
        selectedStudyId,
        handleRowClick,
        closeTabs,
        navigateWithView,
        indicators,
        getIndicators,
      }}
    >
      {children}
    </TableContext.Provider>
  );
};

export default TableProvider;
